// Single source of truth for "is this org on the multi-tenant (multiClient)
// experience?". Route resolution (`src/views/routes.js`), every
// `multiClient/**/routes.js` menu filter, and the calendar's MT endpoint
// switch all read from here, so onboarding an org is one edit, not nine.

// Everything this module exports is configuration, read but never written, and
// the menu filter objects are shared by reference across every route file that
// uses them — one section mutating its own `menuFilters` would otherwise
// silently change every other section's sidebar rule. So freeze all of it.
function deepFreeze(value) {
  if (value && typeof value === 'object') {
    Object.values(value).forEach(deepFreeze);
    Object.freeze(value);
  }
  return value;
}

// Enterprises approved for the multi-tenant experience. Add new ones here as
// their data pipelines go live.
//
// Plain `TimHortons` (legacy pipeline) is deliberately absent; those users
// keep routing to the classic `thRoutes` set. `Tim_Hortons_MT` is the
// multi-tenant TH tenant and belongs here.
export const MT_ENTERPRISES = deepFreeze([
  'Tim_Hortons_MT',
  // "TimHortons",
  'DairyQueen',
  'PeachCobblerFactory',
  'Happy',
  'DavesHotChicken',
  'BurgerFi',
  'SavvySliders',
]);

// Countries where an MT_ENTERPRISES client is served by the multi-tenant stack.
export const MT_COUNTRIES = deepFreeze(['us', 'ca']);

// Orgs on the multi-tenant stack outside the MT_ENTERPRISES × MT_COUNTRIES
// grid, as (enterpriseClient, countryCode) tuples. McDonald's ZA is the first;
// McDonald's elsewhere stays on the single-tenant stack, which is why the tuple
// names the enterprise rather than admitting any ZA org.
//
// Onboarding another country is one line here.
export const MULTICLIENT_ORGS = deepFreeze([
  { enterpriseClient: 'McDonalds', countryCode: 'za' },
]);

/**
 * Whether an org belongs to the multi-tenant (multiClient) experience.
 *
 * True for an MT_ENTERPRISES client in an MT_COUNTRIES country, or for any
 * explicit MULTICLIENT_ORGS tuple.
 *
 * Both arguments are compared case-insensitively and tolerate null/undefined,
 * since they arrive from the root store or a parsed localStorage blob.
 *
 * @param {string} [enterprise] - Org's enterpriseClient, e.g. 'DairyQueen'
 * @param {string} [countryCode] - Org's ISO country code, e.g. 'us'
 * @returns {boolean} True when the org is on the multi-tenant stack
 */
export function isMultiClientUser(enterprise, countryCode) {
  const ec = String(enterprise ?? '').toLowerCase();
  const cc = String(countryCode ?? '').toLowerCase();

  const isMtEnterprise = MT_ENTERPRISES.some((e) => e.toLowerCase() === ec);
  if (isMtEnterprise && MT_COUNTRIES.includes(cc)) {
    return true;
  }

  return MULTICLIENT_ORGS.some(
    (org) =>
      org.enterpriseClient.toLowerCase() === ec &&
      org.countryCode.toLowerCase() === cc,
  );
}

// Sidebar visibility for groups every multiClient org gets — the menuFilters
// equivalent of `isMultiClientUser`. Generated from the lists above so the
// sidebar can never drift from route resolution.
//
// Note these compare `enterpriseClient` with the exact casing the store
// reports, unlike `isMultiClientUser`; the menu filter engine does not
// normalize case.
export const multiClientMenuFilters = deepFreeze({
  $or: [
    {
      $and: [
        { countryCode: { $in: MT_COUNTRIES } },
        { enterpriseClient: { $in: MT_ENTERPRISES } },
      ],
    },
    ...MULTICLIENT_ORGS.map((org) => ({
      $and: [
        { countryCode: { $in: [org.countryCode] } },
        { enterpriseClient: { $in: [org.enterpriseClient] } },
      ],
    })),
  ],
});

// Sidebar visibility for groups that exist only on the MT pipeline — Labor,
// People and Calendar have no MULTICLIENT_ORGS equivalent, so they stay
// scoped to MT_ENTERPRISES in MT_COUNTRIES.
export const mtOnlyMenuFilters = deepFreeze({
  $and: [
    { countryCode: { $in: MT_COUNTRIES } },
    { enterpriseClient: { $in: MT_ENTERPRISES } },
  ],
});
